import React, { useState } from "react";
import { UserOutlined, BookOutlined } from "@ant-design/icons";
import NotMarkedNew from "Assets/Icons/NotMarkedIconNew";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateBadge {
  /** ISO date string e.g. "2024-07-14" */
  date: string;
  /** Number shown on the orange badge */
  count?: number;
}

export interface WeekDateCalendarCardProps {
  /** Start of the visible range */
  startDate: Date | string;
  /** End of the visible range (inclusive) */
  endDate: Date | string;
  /** Controlled active date – if omitted the component manages its own state */
  activeDate?: Date | string;
  /** Called whenever the user clicks a date */
  onDateClick?: (date: Date) => void;
  /** Optional badges to render on specific dates */
  badges?: DateBadge[];
  /** Whether to show the small dot indicator under each date */
  showDots?: boolean;
  /** Today's date in YYYY-MM-DD format (IST). If omitted, uses browser local time */
  todayDate?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Normalise any Date | string to a midnight-local Date */
function toDate(value: Date | string): Date {
  if (value instanceof Date) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Format a date as "YYYY-MM-DD" key for badge lookup */
function toKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Build an array of every date from start → end (inclusive) */
function buildDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// ─── Badged Icon Component ────────────────────────────────────────────────────

interface IndicatorIconsProps {
  showUserIcon?: boolean;
  showAccountBookIcon?: boolean;
  isMobile?: boolean;
}

const IndicatorIcons: React.FC<IndicatorIconsProps> = ({
  showUserIcon,
  showAccountBookIcon,
  isMobile,
}) => {
  if (!showUserIcon && !showAccountBookIcon) return null;
  const boxWidth = isMobile ? "18px" : "30px";
  const boxHeight = isMobile ? "18px" : "28px";
  const iconFontSize = isMobile ? "10px" : "14px";
  const badgeWidth = isMobile ? "10px" : "14px";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? "2px" : "6px",
        marginTop: "4px",
      }}
    >
      {showUserIcon && (
        <div
          style={{
            width: boxWidth,
            height: boxHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            boxSizing: "border-box",
            overflow: "visible",
            position: "relative",
            fontSize: isMobile ? "10px" : undefined,
          }}
        >
          <UserOutlined style={{ fontSize: iconFontSize }} />
          <NotMarkedNew
            style={{
              position: "absolute",
              width: badgeWidth,
              bottom: "-3px",
              right: "-3px",
            }}
          />
        </div>
      )}

      {showAccountBookIcon && (
        <div
          style={{
            width: boxWidth,
            height: boxHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "50%",
            boxSizing: "border-box",
            overflow: "visible",
            position: "relative",
            fontSize: isMobile ? "10px" : undefined,
          }}
        >
          <BookOutlined style={{ fontSize: iconFontSize }} />
          <NotMarkedNew
            style={{
              position: "absolute",
              width: badgeWidth,
              bottom: "-3px",
              right: "-3px",
            }}
          />
        </div>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, any> = {
  wrapper: {
    mobile: {
      flex: 1,
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      padding: "4px 8px 10px",
    },
    desktop: {
      display: "grid",
      gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
      gap: "12px",
      width: "100%",
    },
  },
  cell: {
    mobile: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      minWidth: "20px",
      flexShrink: 0,
      cursor: "pointer",
      userSelect: "none",
      borderRadius: "12px",
      padding: "8px 10px 10px",
      transition: "background 0.2s ease",
      position: "relative",
    },
    desktop: {
      width: "100%",
      minWidth: 0,
      height: "clamp(72px, 9vw, 100px)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "16px",
      transition: "0.2s ease",
      cursor: "pointer",
      position: "relative",
    },
  },
  dayLabel: {
    fontSize: "12px",
    fontWeight: 800,
    marginBottom: "4px",
    letterSpacing: "0.2px",
  },
  dateNumber: {
    lineHeight: 1.1,
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "600",
    marginTop: "2px",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#00b0f5",
    marginTop: "6px",
  },
  badge: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#f5a623",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "6px",
  },
  todayRing: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "2px solid #00b0f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const WeekDateCalendarCard: React.FC<WeekDateCalendarCardProps> = ({
  startDate,
  endDate,
  activeDate,
  onDateClick,
  badges = [],
  showDots = true,
  todayDate,
}) => {
  const { fullSchedules } = useScheduleBetaContext();
  const start = toDate(startDate);
  const end = toDate(endDate);
  const today = todayDate ? toDate(todayDate) : toDate(new Date());

  // Build badge lookup map
  const dateDataMap: Record<string, DateBadge> = {};
  badges.forEach((item) => {
    dateDataMap[item.date] = item;
  });

  // Internal active state (used when prop is not controlled)
  const [internalActive, setInternalActive] = useState<string>(toKey(today));

  const activeDateKey = activeDate ? toKey(toDate(activeDate)) : internalActive;
  const { screen } = useWindowSize();

  const dates = buildDateRange(start, end);

  const handleClick = (date: Date) => {
    setInternalActive(toKey(date));
    onDateClick?.(date);
  };

  const isDesktop = ["xl", "xxl", "lg"].includes(screen);
  const isMobile = ["xs", "sm", "md"].includes(screen);
  const wrapper = isDesktop ? styles.wrapper.desktop : styles.wrapper.mobile;
  const cell = isDesktop ? styles.cell.desktop : styles.cell.mobile;

  return (
    <div
      style={{
        ...wrapper,
        gap: isMobile ? "2px" : "4px",
        padding: isDesktop ? "8px 12px" : isMobile ? "4px 8px" : "6px 10px",
      }}
    >
      {dates.map((date) => {
        const key = toKey(date);
        const dayIdx = date.getDay();
        const dayLabel = DAY_LABELS[dayIdx];
        const dateNum = date.getDate();
        const isSunday = dayIdx === 0;
        const dayLectures: any[] = fullSchedules?.[key] || [];
        const showUserIcon = dayLectures.some(
          (lecture) => lecture.attendanceStatus === "Unmarked"
        );
        const showAccountBookIcon = dayLectures.some(
          (lecture) => lecture.syllabusStatus === "Unmarked"
        );
        const isActive = key === activeDateKey;
        const isToday = key === toKey(today);
        const dayConfig = dateDataMap[key];
        const badgeCount = dayConfig?.count;
        const activeBg = "#008dc4";
        const activeDayLabelColor = "#ffffff";
        const activeDateColor = "#ffffff";
        const inactiveDayLabelColor = isSunday ? "#e53935" : "#9e9e9e";
        const inactiveDateColor = isSunday ? "#e53935" : "#212121";

        return (
          <div
            key={key}
            style={{
              ...cell,
              backgroundColor: "transparent",
            }}
            onClick={() => handleClick(date)}
          >
            {/* Day label — Mon / Tue … */}
            <span
              style={{
                ...styles.dayLabel,
                fontSize: isMobile ? "10px" : "16px",
                color: isSunday ? "#e53935" : "#9e9e9e",
                fontWeight: isActive ? 700 : 800,
              }}
            >
              {dayLabel}
            </span>

            {/* Date number — with today ring when not active */}
            {isToday && !isActive ? (
              <div
                style={{
                  ...styles.todayRing,
                  width: isMobile ? "24px" : "30px",
                  height: isMobile ? "24px" : "30px",
                  borderColor: isSunday ? "#e53935" : "#00b0f5",
                }}
              >
                <span
                  style={{
                    ...styles.dateNumber,
                    fontSize: isMobile ? "12px" : "16px",
                    color: isActive
                      ? activeDayLabelColor
                      : inactiveDayLabelColor,
                  }}
                >
                  {dateNum}
                </span>
              </div>
            ) : (
              <span
                style={{
                  ...styles.dateNumber,
                  width: isMobile ? "24px" : "30px",
                  height: isMobile ? "24px" : "30px",
                  fontSize: isMobile ? "12px" : "14px",
                  color: isActive ? activeDateColor : inactiveDateColor,
                  backgroundColor: isActive ? activeBg : "transparent",
                  borderRadius: "50%",
                  padding: "4px",
                }}
              >
                {dateNum}
              </span>
            )}

            {/* Badge, Icons (replacing dot if present), or standard Dot */}
            {badgeCount !== undefined && badgeCount > 0 ? (
              <div style={styles.badge}>
                {badgeCount > 9 ? "9+" : badgeCount}
              </div>
            ) : !isSunday && (showUserIcon || showAccountBookIcon) ? (
              <IndicatorIcons
                showUserIcon={showUserIcon}
                showAccountBookIcon={showAccountBookIcon}
                isMobile={isMobile}
              />
            ) : showDots ? (
              <div
                style={{
                  ...styles.dot,
                  backgroundColor: isSunday ? "transparent" : "#008dc4",
                  marginTop: isToday && !isActive ? "6px" : "6px",
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default WeekDateCalendarCard;
